import type {
	API_CategoryResponse,
	API_CreateCategoryBody,
	API_CreateExpenseBody,
	API_CreateRecurringExpenseBody,
	API_ExpenseResponse,
	API_GetCategoriesResponse,
	API_GetCostAnalysisResponse,
	API_GetExpensesQuery,
	API_GetExpensesResponse,
	API_GetRecurringExpensesResponse,
	API_MarkExpensePaidBody,
	API_RecurringExpenseResponse,
	API_UpdateCategoryBody,
	API_UpdateExpenseBody,
	API_UpdateRecurringExpenseBody,
	CostAnalysisResponseData,
	ExpenseCategoryDto,
	ExpenseDto,
	FinancialAnalysisGranularity,
	RecurringExpenseDto,
} from "@pilatestime/shared-types";
import { queryOptions } from "@tanstack/react-query";
import api from "./api_client";

export interface CostManagementRangeParams {
	fromDate?: string;
	toDate?: string;
}

const authHeaders = (token: string) => ({
	headers: { Authorization: `Bearer ${token}` },
});

const buildRangeQuery = (range: CostManagementRangeParams): string => {
	const parts: string[] = [];
	if (range.fromDate)
		parts.push(`fromDate=${encodeURIComponent(range.fromDate)}`);
	if (range.toDate) parts.push(`toDate=${encodeURIComponent(range.toDate)}`);
	return parts.length > 0 ? `&${parts.join("&")}` : "";
};

// ============================================================
// Cost analysis
// ============================================================

export const fetchCostAnalysis = (
	token: string,
	organizationId: string,
	granularity: FinancialAnalysisGranularity,
	range: CostManagementRangeParams,
): Promise<CostAnalysisResponseData> =>
	api
		.get(
			`/organizations/${organizationId}/org-management/cost-management/analysis?granularity=${granularity}${buildRangeQuery(range)}`,
			authHeaders(token),
		)
		.then((res) => (res.data as API_GetCostAnalysisResponse).data);

export const fetchCostAnalysisQueryOptions = (
	organizationId: string,
	token: string,
	granularity: FinancialAnalysisGranularity,
	range: CostManagementRangeParams,
) =>
	queryOptions({
		queryKey: [
			"cost_analysis",
			organizationId,
			granularity,
			range.fromDate ?? null,
			range.toDate ?? null,
		],
		queryFn: () => fetchCostAnalysis(token, organizationId, granularity, range),
		staleTime: 5 * 60 * 1000,
		gcTime: 10 * 60 * 1000,
		retry: 2,
		retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30000),
	});

// ============================================================
// Expenses
// ============================================================

export const buildExpensesQuery = (filters: API_GetExpensesQuery): string => {
	const parts: string[] = [];
	if (filters.fromDate)
		parts.push(`fromDate=${encodeURIComponent(filters.fromDate)}`);
	if (filters.toDate)
		parts.push(`toDate=${encodeURIComponent(filters.toDate)}`);
	if (filters.categoryIds) {
		for (const id of filters.categoryIds) {
			parts.push(`categoryIds=${encodeURIComponent(id)}`);
		}
	}
	if (filters.status) parts.push(`status=${filters.status}`);
	if (filters.page !== undefined) parts.push(`page=${filters.page}`);
	if (filters.limit !== undefined) parts.push(`limit=${filters.limit}`);
	return parts.length > 0 ? `?${parts.join("&")}` : "";
};

export const fetchExpenses = (
	token: string,
	organizationId: string,
	filters: API_GetExpensesQuery,
): Promise<API_GetExpensesResponse["data"]> =>
	api
		.get(
			`/organizations/${organizationId}/org-management/cost-management/expenses${buildExpensesQuery(filters)}`,
			authHeaders(token),
		)
		.then((res) => (res.data as API_GetExpensesResponse).data);

export const fetchExpensesQueryOptions = (
	organizationId: string,
	token: string,
	filters: API_GetExpensesQuery,
) =>
	queryOptions({
		queryKey: [
			"expenses",
			organizationId,
			filters.fromDate ?? null,
			filters.toDate ?? null,
			filters.categoryIds ?? null,
			filters.status ?? null,
			filters.page ?? 1,
			filters.limit ?? 25,
		],
		queryFn: () => fetchExpenses(token, organizationId, filters),
		staleTime: 5 * 60 * 1000,
		gcTime: 10 * 60 * 1000,
		retry: 2,
	});

export const createExpense = (
	token: string,
	organizationId: string,
	body: API_CreateExpenseBody,
): Promise<ExpenseDto> =>
	api
		.post(
			`/organizations/${organizationId}/org-management/cost-management/expenses`,
			body,
			authHeaders(token),
		)
		.then((res) => (res.data as API_ExpenseResponse).data);

export const updateExpense = (
	token: string,
	organizationId: string,
	expenseId: string,
	body: API_UpdateExpenseBody,
): Promise<ExpenseDto> =>
	api
		.patch(
			`/organizations/${organizationId}/org-management/cost-management/expenses/${expenseId}`,
			body,
			authHeaders(token),
		)
		.then((res) => (res.data as API_ExpenseResponse).data);

export const markExpensePaid = (
	token: string,
	organizationId: string,
	expenseId: string,
	body: API_MarkExpensePaidBody,
): Promise<ExpenseDto> =>
	api
		.post(
			`/organizations/${organizationId}/org-management/cost-management/expenses/${expenseId}/mark-paid`,
			body,
			authHeaders(token),
		)
		.then((res) => (res.data as API_ExpenseResponse).data);

export const cancelExpense = (
	token: string,
	organizationId: string,
	expenseId: string,
): Promise<ExpenseDto> =>
	api
		.delete(
			`/organizations/${organizationId}/org-management/cost-management/expenses/${expenseId}`,
			authHeaders(token),
		)
		.then((res) => (res.data as API_ExpenseResponse).data);

// ============================================================
// CSV export — plain blob, not React Query
// ============================================================

// Pull the filename out of `Content-Disposition: attachment; filename="..."`
// — handles quoted and unquoted forms per RFC 6266 §4.1. Falls back to a
// client-side dated name when the header is missing (e.g. CORS misconfig).
const parseContentDispositionFilename = (header: unknown): string | null => {
	if (typeof header !== "string") return null;
	const quoted = header.match(/filename="([^"]+)"/i);
	if (quoted) return quoted[1];
	const bare = header.match(/filename=([^;]+)/i);
	return bare ? bare[1].trim() : null;
};

export const downloadExpensesCsv = async (
	token: string,
	organizationId: string,
	filters: API_GetExpensesQuery,
): Promise<void> => {
	const response = await api.get(
		`/organizations/${organizationId}/org-management/cost-management/expenses.csv${buildExpensesQuery(filters)}`,
		{
			...authHeaders(token),
			responseType: "blob",
		},
	);
	const blob = response.data as Blob;
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	const filename =
		parseContentDispositionFilename(response.headers["content-disposition"]) ??
		`expenses-${new Date().toISOString().slice(0, 10)}.csv`;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
};

// ============================================================
// Categories
// ============================================================

export const fetchExpenseCategories = (
	token: string,
	organizationId: string,
): Promise<ExpenseCategoryDto[]> =>
	api
		.get(
			`/organizations/${organizationId}/org-management/cost-management/categories`,
			authHeaders(token),
		)
		.then((res) => (res.data as API_GetCategoriesResponse).data.categories);

export const fetchExpenseCategoriesQueryOptions = (
	organizationId: string,
	token: string,
) =>
	queryOptions({
		queryKey: ["expense_categories", organizationId],
		queryFn: () => fetchExpenseCategories(token, organizationId),
		staleTime: 30 * 60 * 1000,
		gcTime: 60 * 60 * 1000,
		retry: 2,
	});

export const createCategory = (
	token: string,
	organizationId: string,
	body: API_CreateCategoryBody,
): Promise<ExpenseCategoryDto> =>
	api
		.post(
			`/organizations/${organizationId}/org-management/cost-management/categories`,
			body,
			authHeaders(token),
		)
		.then((res) => (res.data as API_CategoryResponse).data);

export const updateCategory = (
	token: string,
	organizationId: string,
	categoryId: string,
	body: API_UpdateCategoryBody,
): Promise<ExpenseCategoryDto> =>
	api
		.patch(
			`/organizations/${organizationId}/org-management/cost-management/categories/${categoryId}`,
			body,
			authHeaders(token),
		)
		.then((res) => (res.data as API_CategoryResponse).data);

export const archiveCategory = (
	token: string,
	organizationId: string,
	categoryId: string,
): Promise<ExpenseCategoryDto> =>
	api
		.delete(
			`/organizations/${organizationId}/org-management/cost-management/categories/${categoryId}`,
			authHeaders(token),
		)
		.then((res) => (res.data as API_CategoryResponse).data);

// ============================================================
// Recurring
// ============================================================

export const fetchRecurringExpenses = (
	token: string,
	organizationId: string,
): Promise<RecurringExpenseDto[]> =>
	api
		.get(
			`/organizations/${organizationId}/org-management/cost-management/recurring`,
			authHeaders(token),
		)
		.then(
			(res) =>
				(res.data as API_GetRecurringExpensesResponse).data.recurringExpenses,
		);

export const fetchRecurringExpensesQueryOptions = (
	organizationId: string,
	token: string,
) =>
	queryOptions({
		queryKey: ["recurring_expenses", organizationId],
		queryFn: () => fetchRecurringExpenses(token, organizationId),
		staleTime: 5 * 60 * 1000,
		gcTime: 10 * 60 * 1000,
		retry: 2,
	});

export const createRecurringExpense = (
	token: string,
	organizationId: string,
	body: API_CreateRecurringExpenseBody,
): Promise<RecurringExpenseDto> =>
	api
		.post(
			`/organizations/${organizationId}/org-management/cost-management/recurring`,
			body,
			authHeaders(token),
		)
		.then((res) => (res.data as API_RecurringExpenseResponse).data);

export const updateRecurringExpense = (
	token: string,
	organizationId: string,
	recurringId: string,
	body: API_UpdateRecurringExpenseBody,
): Promise<RecurringExpenseDto> =>
	api
		.patch(
			`/organizations/${organizationId}/org-management/cost-management/recurring/${recurringId}`,
			body,
			authHeaders(token),
		)
		.then((res) => (res.data as API_RecurringExpenseResponse).data);

export const archiveRecurringExpense = (
	token: string,
	organizationId: string,
	recurringId: string,
): Promise<RecurringExpenseDto> =>
	api
		.delete(
			`/organizations/${organizationId}/org-management/cost-management/recurring/${recurringId}`,
			authHeaders(token),
		)
		.then((res) => (res.data as API_RecurringExpenseResponse).data);

// ============================================================
// Cache keys (centralized — see useCostManagementMutations.ts)
// ============================================================

export const costManagementQueryKeys = {
	all: (organizationId: string) => ["cost_management", organizationId] as const,
	analysis: (organizationId: string) =>
		["cost_analysis", organizationId] as const,
	expenses: (organizationId: string) => ["expenses", organizationId] as const,
	categories: (organizationId: string) =>
		["expense_categories", organizationId] as const,
	recurring: (organizationId: string) =>
		["recurring_expenses", organizationId] as const,
	financialAnalysis: (organizationId: string) =>
		["org_mgmt_financial_analysis", organizationId] as const,
};
